import { FC, MutableRefObject, useEffect, useRef, useState } from 'react';

import { Button } from 'antd';

import Attribution from './attribution';
import Details from './details';
import UniversalFilter from './filter';
import ReportFilter from './filter/ReportFilter';
import CompareSection from './filter/compare';
import PeriodSelector, { PeriodInterval } from './period';
import { IAttributionItem, IDetailItem } from '../../../../../app/types/filter';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import { ReportFilterType } from './filter/types';

interface IParamsProps {
  withAttribution?: boolean;
  attributionItems?: IAttributionItem[];
  withDetails?: boolean;
  detailsItems?: IDetailItem[];
  /** @deprecated Кнопка сравнения теперь в заголовке отчёта */
  withCompare?: boolean;
  withSegments?: boolean;
  /** @deprecated Используйте reportType вместо filtersFilter */
  filtersFilter?: string[];
  /** Тип отчёта для автоматического определения категорий фильтров */
  reportType?: ReportFilterType;
  isFilterVisible?: boolean;
  defaultPeriod?: PeriodInterval;
  isMountReportRef?: MutableRefObject<boolean>;
  /** Использовать новый компонент ReportFilter */
  useNewFilter?: boolean;
}

const Params: FC<IParamsProps> = ({
  withAttribution = true,
  attributionItems,
  withDetails = true,
  detailsItems,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  withCompare = true, // Deprecated: кнопка сравнения теперь в заголовке
  withSegments = true,
  filtersFilter,
  reportType,
  isFilterVisible = true,
  defaultPeriod,
  isMountReportRef = { current: false },
  useNewFilter = false, // Временно отключён новый фильтр до исправления ошибок
}) => {
  const { isCompare } = useTypedSelector(
    (state) => state.meta
  );
  const { isFixedHeader, isOpenFixedHeader } = useTypedSelector(
    (state) => state.dashboard
  );

  const [ btnDisabled, setBtnDisabled ] = useState(true);

  const { attribution, type_applications } = useTypedSelector((state) => state.attributes.attributes);
  const period = useTypedSelector((state) => state.period);
  const { filters } = useTypedSelector((state) => state.filter);
  const { details } = useTypedSelector((state) => state.details);

  // Используем ref для отслеживания предыдущих значений вместо JSON.stringify
  const prevValuesRef = useRef<{
    filters: typeof filters;
    attribution: typeof attribution;
    type_applications: typeof type_applications;
    period: typeof period;
    details: typeof details;
  } | null>(null);

  useEffect(() => {
      const prevValues = prevValuesRef.current;
      const hasChanged = !prevValues || 
        prevValues.filters !== filters ||
        prevValues.attribution !== attribution ||
        prevValues.type_applications !== type_applications ||
        prevValues.period !== period ||
        prevValues.details !== details;

      if (hasChanged) {
        prevValuesRef.current = { filters, attribution, type_applications, period, details };
        
        if(!isMountReportRef?.current) {
          setBtnDisabled(false);
        } else {
          setBtnDisabled(true);
          setTimeout(() => { isMountReportRef.current = false; }, 2000);
        }
      }
    }, [ filters, attribution, type_applications, period, details, isMountReportRef ]);
  

  const isParamsVisible = !isFixedHeader || isOpenFixedHeader;
  const showAttribution = Boolean(withAttribution && attributionItems && isParamsVisible);
  const showDetails = Boolean(withDetails && detailsItems && isParamsVisible);

  // Отправка запроса из блока сравнения — кнопка «Показать» вынесена в строку параметров
  const submitCompareRef = useRef<(() => void) | null>(null);

  return (
    <div
      className="dashboard-params-compact"
      style={{
        width: '100%',
        marginLeft: '-24px',
        padding: '15px 15px 0px 25px',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '8px',
        ...(isFixedHeader
          ? {
              width: 'fit-content',
              paddingTop: 0,
            }
          : {}),
      }}
    >
      {!isCompare && <span data-help-id="params-period"><PeriodSelector isCompare={isCompare} exInterval={defaultPeriod}/></span>}
      {/* Общие параметры отчёта. Позиция в дереве неизменна в обоих режимах —
        * иначе Attribution/Details перемонтируются и сбрасывают выбранные значения.
        * В сравнении строка визуально уезжает под блоки за счёт order */}
      <div
        style={{
          // Вне сравнения обёртка не участвует в раскладке (display: contents),
          // поэтому строка параметров выглядит ровно как раньше — без обёртки
          display: isCompare ? 'flex' : 'contents',
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '8px',
          ...(isCompare ? { order: 2, width: '100%' } : {}),
        }}
      >
        {showAttribution && attributionItems && (
          <Attribution items={attributionItems}/>
        )}
        {showDetails && detailsItems && (
          <span data-help-id="params-details"><Details items={detailsItems}/></span>
        )}
        {isCompare && (
          <Button
            type="primary"
            className="request_button"
            onClick={() => submitCompareRef.current?.()}
          >
            Показать
          </Button>
        )}
      </div>
      {/* Кнопка сравнения перенесена в UniversalReportTitle */}
      {isCompare ? (
        <span
          data-help-id="params-compare"
          style={{ display: 'block', flex: '1 1 100%', width: '100%', order: 1 }}
        >
          <CompareSection
            filtersFilter={filtersFilter}
            hideSubmitButton
            submitRef={submitCompareRef}
          />
        </span>
      ) : (
        isFilterVisible && (
          <>
          {useNewFilter ? (
            <ReportFilter
              reportType={reportType}
              filtersFilter={filtersFilter}
              reqBtnDisabled={btnDisabled}
              setReqBtnDisabled={setBtnDisabled}
              isCompare={isCompare}
              withSegment={withSegments}
              withRefresh={true}
            />
          ) : (
            <UniversalFilter
              reqBtnDisabled={btnDisabled}
              setReqBtnDisabled={setBtnDisabled}
              isCompare={isCompare}
              withSegment={withSegments}
              filtersFilter={filtersFilter}
              withRefresh={true}
            />
          )}
          </>
        )
      )}
    </div>
  );
};

export default Params;
